// The brand configuration's runtime values, as entries of the settings renderer (spec/14 §14,
// spec/03 §12.1; F-HQ-38, F-ORG-04).
//
// `GET/PUT /v1/hq/brand` is one resource with one revision (its `ETag`): five groups HQ edits — the
// support contacts, the legal documents with their versions in force, the design tokens, the kiosk's
// default media, and the limits on organisation branding. Each group is one entry here:
//
//   value        what HQ has set, or else the deployment's (what clients get)
//   source       `platform` when HQ has set it, `default` when it is the deployment's
//   reset        hands the group back to the deployment (`reset: [group]` on the wire)
//
// so the renderer draws it as it draws a setting — its control built from the contract's own schema
// (`BrandRuntimeChange`, picked out of the OpenAPI document at build time, vite/contract-schemas.ts),
// one Save for the screen, `If-Match` on every save, and the 412 panel when someone saved first. The
// server refuses a colour below WCAG AA with the pair named; that refusal is filed under the field.

import type { ApiClient, BrandRuntimeChange, HqBrandView } from '@clavian/api-client';
import { $defs, brandFonts } from 'virtual:clavian/contract-schemas';

import { versioned, type Versioned } from '../api/versioned';
import { DEFAULT_COUNTRY } from '../i18n';
import type { JsonSchema, SchemaContext } from '../settings/schema';
import type { RenderableSetting, SettingChange, SettingsPart } from '../settings/types';

/** The groups HQ edits, in the order its screen shows them (`BRAND_RUNTIME_GROUPS` in the contract). */
export const BRAND_GROUPS = ['support', 'legal', 'theme', 'kioskScreensaver', 'organizationBranding'] as const;
export type BrandGroup = (typeof BRAND_GROUPS)[number];

function changeSchema(): JsonSchema {
  const schema = $defs['BrandRuntimeChange'];
  if (schema === undefined) throw new Error('The contract has no BrandRuntimeChange schema (vite/contract-schemas.ts).');
  return schema;
}

/**
 * The fonts as choices. The contract checks a font id against the list by refinement, so its schema
 * is a string; the screen offers the list itself, split by what each family may be used for.
 */
function brandContext(): SchemaContext {
  const ids = (role: 'display' | 'body') => brandFonts.filter((font) => font.roles.includes(role)).map((font) => font.id);
  return {
    defs: $defs,
    hints: {
      BrandFonts: {
        properties: {
          display: { type: 'string', enum: ids('display') },
          body: { type: 'string', enum: ids('body') },
        },
      },
    },
    defaultCountry: DEFAULT_COUNTRY,
  };
}

/** A font id's family name, for its label; the id itself for one not on the list. */
export function fontFamily(id: string): string {
  return brandFonts.find((font) => font.id === id)?.family ?? id;
}

export interface BrandWords {
  readonly label: (group: BrandGroup) => string;
}

export function brandEntries(view: HqBrandView, words: BrandWords): RenderableSetting[] {
  const properties = (changeSchema()['properties'] ?? {}) as Record<string, JsonSchema>;
  const context = brandContext();
  return BRAND_GROUPS.map((group) => {
    const set = view.values[group];
    const deployment = view.deployment[group];
    return {
      key: group,
      label: words.label(group),
      // Each group is its own panel, headed by its name.
      section: words.label(group),
      schema: properties[group] ?? {},
      context,
      value: set ?? deployment,
      source: set === undefined ? 'default' : 'platform',
      ...(set === undefined ? {} : { inherited: { value: deployment, source: 'default' as const } }),
      hasOverride: set !== undefined,
      // The page is shown only to a role holding `hq:settings`, which is what the route asks for.
      editable: true,
    };
  });
}

export function readBrand(api: ApiClient): Promise<Versioned<HqBrandView>> {
  return versioned((onResponse) => api.getHqBrand({ onResponse }));
}

/** The changes as one `BrandRuntimeChange`: each group set to its value, each reset one handed back. */
export function brandChange(changes: readonly SettingChange[]): BrandRuntimeChange {
  const body: Record<string, unknown> = {};
  const reset: string[] = [];
  for (const change of changes) {
    if (change.reset) reset.push(change.key);
    else body[change.key] = change.value;
  }
  return { ...body, ...(reset.length === 0 ? {} : { reset }) } as BrandRuntimeChange;
}

export function writeBrand(api: ApiClient, changes: readonly SettingChange[], etag: string): Promise<Versioned<HqBrandView>> {
  return versioned((onResponse) => api.putHqBrand({ headerParams: { 'if-match': etag }, body: brandChange(changes), onResponse }));
}

export function brandPart(options: {
  readonly api: ApiClient;
  readonly view: Versioned<HqBrandView>;
  readonly words: BrandWords;
  /** Told of every fresh version, so the query cache holds what the form holds. */
  readonly onVersion: (view: Versioned<HqBrandView>) => void;
}): SettingsPart {
  const { api, words, onVersion } = options;
  const entriesOf = (view: Versioned<HqBrandView>) => ({ data: brandEntries(view.data, words), etag: view.etag });
  return {
    id: 'brand',
    entries: brandEntries(options.view.data, words),
    etag: options.view.etag,
    save: async (changes, etag) => {
      const next = await writeBrand(api, changes, etag);
      onVersion(next);
      return entriesOf(next);
    },
    reload: async () => {
      const next = await readBrand(api);
      onVersion(next);
      return entriesOf(next);
    },
    // The server files a group's field errors under its own path (`theme.colour.primary`), which is
    // how the renderer files them too.
    fieldKey: (field) => field,
  };
}
